import {useVatService} from '@/vat/vat.service'
import {useTransactionCreator} from '@/transaction/create-transaction/TransactionCreator.service'
import {parse} from 'date-fns'
import {bookkeepingAccounts} from '@/bookkeeping-account/bookkeepingAccount.service'
import Fuse from 'fuse.js'
import {customers, suppliers} from '@/subaccount/subAccount.service'
import {CUSTOMER, SubAccount} from '@/subaccount/types/SubAccount'
import {BookkeepingAccount} from '@/bookkeeping-account/BookkeepingAccount'
import {CUSTOMER_ACCOUNT_NO, SUPPLIER_ACCOUNT_NO} from '@/bookkeeping-account/accountTypes'
import {TransactionEntryLine} from '@/types/transaction/TransactionEntryLine'
import {useDimensions} from '@/dimensions/dimensions.service'
import {defaultVatOption} from '@/vat/types/VatOptions'
import {EntryLineFields} from '@/csv-transaction-importer/csv-import.service'
import {v4 as uuidv4} from 'uuid'

const tryParseDateFromString = (dateString: string): Date => {
    const formats = ['yyyy-MM-dd', 'dd.MM.yyyy', 'dd/MM/yyyy']
    for (const format of formats) {
        const parsedDate = parse(dateString, format, new Date())
        if (!isNaN(parsedDate.getTime())) {
            return parsedDate
        }
    }
    return new Date()
}

const findAccountOrSubAccount = (account: string, fuse: Fuse<SubAccount>): BookkeepingAccount | SubAccount | undefined => {
    const regex = /^\d{4}$/
    if (regex.test(account)) {
        const maybeFound = bookkeepingAccounts.value.find(acc => acc.accountNo === account)
        if (maybeFound) {
            return maybeFound
        }
    }
    const fuseResults = fuse.search(account)
    if (fuseResults.length) {
        return fuseResults[0].item
    }
}

export const writeFieldListToTransactionEntries = (entries: EntryLineFields[]) => {
    const fuse = new Fuse([...customers.value, ...suppliers.value], {keys: ['name'], threshold: 0.2, shouldSort: false})
    const {findVatOptionFromCode} = useVatService()
    const {transactionEntryLines} = useTransactionCreator()
    const {projects, departments} = useDimensions()

    const entryLines = entries.map((fields: EntryLineFields, index: number) => {
        const entry: TransactionEntryLine = {
            entryUuid: uuidv4(),
            date: tryParseDateFromString(fields.date),
            debitAccount: '',
            vatOptionIdDebit: findVatOptionFromCode(parseFloat(fields.debitVat))?.id || defaultVatOption.id,
            vatOptionIdCredit: findVatOptionFromCode(parseFloat(fields.creditVat))?.id || defaultVatOption.id,
            subAccountLines: [],
            description: fields.description,
            tags: [],
            amount: parseFloat(fields.amount.replace(',', '.')),
            entryLineIndex: index
        }

        const debit: BookkeepingAccount | SubAccount | undefined = findAccountOrSubAccount(fields.debitAccount, fuse)
        if (debit && (debit as BookkeepingAccount).accountNo) {
            entry.debitAccount = (debit as BookkeepingAccount).accountNo
        } else if (debit && (debit as SubAccount).subAccountNo) {
            const subAcc = (debit as SubAccount)
            entry.debitAccount = subAcc.relationType === CUSTOMER ? CUSTOMER_ACCOUNT_NO : SUPPLIER_ACCOUNT_NO
            entry.debitSubAccount = subAcc.uuid
        }

        const credit: BookkeepingAccount | SubAccount | undefined = findAccountOrSubAccount(fields.creditAccount, fuse)
        if (credit && (credit as BookkeepingAccount).accountNo) {
            entry.creditAccount = (credit as BookkeepingAccount).accountNo
        } else if (credit && (credit as SubAccount).subAccountNo) {
            const subAcc = (credit as SubAccount)
            entry.creditAccount = subAcc.relationType === CUSTOMER ? CUSTOMER_ACCOUNT_NO : SUPPLIER_ACCOUNT_NO
            entry.creditSubAccount = subAcc.uuid
        }

        if (fields.project) {
            const projectDimension = projects.value.find(d => d.name === fields.project)
            if (projectDimension) {
                entry.project = projectDimension.name
            }
        }

        if (fields.department) {
            const departmentDimension = departments.value.find(d => d.name === fields.department)
            if (departmentDimension) {
                entry.department = departmentDimension.name
            }
        }

        entry.accrualAccountNo = fields.accrAcc || undefined
        entry.accrualStartYear = fields.accrStartYear ? parseInt(fields.accrStartYear) : undefined
        entry.accrualStartMonth = fields.accrStartMonthOneIndexed ? parseInt(fields.accrStartMonthOneIndexed) - 1 : undefined
        entry.accrualDuration = fields.accrDuration ? parseInt(fields.accrDuration) : undefined

        return entry
    })
    transactionEntryLines.value = [...entryLines]
}